import config from 'config';
import React from 'react';
import styled, { css, useTheme } from 'styled-components';

import { LogoIcon } from 'components/Icons/LogoIcon';
import LogoWithRights from 'components/Icons/LogoWithRights';
import getDeviceInfo, { DeviceType } from 'helpers/getDeviceInfo';

import StoreButtons from './StoreButtons';

const LOGO_WIDTH = 70;
const LOGO_HEIGHT = 44;
const CURR_YEAR = new Date().getFullYear();

type Props = {
  withGetAppSection?: boolean;
};

const WebsiteFooter = ({ withGetAppSection }: Props) => {
  const theme = useTheme();

  const { platform } = getDeviceInfo();
  const isDesktop = platform !== DeviceType.Mobile;

  return (
    <Container>
      {isDesktop && !!withGetAppSection && (
        <GetAppContainer>
          <GetAppTitle>Get the app</GetAppTitle>
          <StoresContainer>
            <StoreButtons />
          </StoresContainer>
        </GetAppContainer>
      )}
      <Content>
        <LogoWrapper>
          {isDesktop ? (
            <LogoIcon
              width={LOGO_WIDTH}
              height={LOGO_HEIGHT}
              fill={theme.colors.white}
            />
          ) : (
            <>
              <LogoWithRights color={theme.colors.white} />
              <CompanyMark>© {CURR_YEAR} Shido Group Ltd.</CompanyMark>
            </>
          )}
        </LogoWrapper>
        {
          <>
            <DashedLine />
            <NavbarElements>
              <CompanyMark $hideOnMobile>
                Copyright © {CURR_YEAR} Shido Group Ltd.
              </CompanyMark>
              <FooterLink
                href={`${config.shidoWebsiteVersion}`}
                target="_blank"
                rel="noreferrer"
              >
                Shido.com
              </FooterLink>
              <FooterLink
                href={`${config.shidoWebsiteVersion}/support`}
                target="_blank"
                rel="noreferrer"
              >
                Support
              </FooterLink>
              <FooterLink
                href={`${config.shidoWebsiteVersion}/privacy-policy`}
                target="_blank"
                rel="noreferrer"
              >
                Privacy policy
              </FooterLink>
              <FooterLink
                href={`${config.shidoWebsiteVersion}/terms-and-conditions`}
                target="_blank"
                rel="noreferrer"
              >
                Terms & Conditions
              </FooterLink>
            </NavbarElements>
          </>
        }
      </Content>
    </Container>
  );
};

const GetAppContainer = styled.div`
  width: 100%;
  padding: 70px 0 78px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.blueBayoux};
`;

const StoresContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 19px;
`;

const GetAppTitle = styled.h3`
  font-size: 28px;
  font-weight: 400;
  margin-bottom: 36.5px;
  color: ${({ theme }) => theme.colors.white};
`;

const Container = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.primary};
`;

const Content = styled.article`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 0 195px;
  background-color: ${({ theme }) => theme.colors.primary};

  ${({ theme }) => theme.breakpoints.down('lg')} {
    padding: 51px 0 100px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-top: 22px;
    padding-bottom: 60px;
    align-items: flex-start;
  }
`;

const LogoWrapper = styled.div`
  margin-bottom: 38px;
  position: relative;

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-top: 0;
    padding-left: 0;
    margin-top: 78px;
    margin-bottom: 0;
    order: 2;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }
`;

const DashedLine = styled.div`
  width: 1400px;
  height: 1px;
  background-image: linear-gradient(to right, transparent 50%, white 50%);
  background-size: 8px 100%;
  opacity: 0.4;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    width: 900px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const NavbarElements = styled.nav`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 22px;
  justify-content: center;
  margin: 37px 0 28px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: flex;
    flex-direction: column;
    margin: 0;
    width: 100%;
    gap: 0;
  }
`;

const CompanyMark = styled.p<{ $hideOnMobile?: boolean }>`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.white};

  ${({ theme }) => theme.breakpoints.down('md')} {
    ${({ $hideOnMobile }) =>
      $hideOnMobile &&
      css`
        display: none;
      `}
  }
`;

const FooterLink = styled.a`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.darkBlueV3};
    border-bottom: 1px dashed rgba(255 255 255 / 25%);
    padding: 14px 0;
    margin: 0 20px;
  }
`;

export default WebsiteFooter;
